/**
 * EventsPanel.tsx — Right-side panel for adding life events to the simulation.
 *
 * Events modify the Monte Carlo engine per-year: income changes, expenses,
 * portfolio shocks, windfalls, etc.
 */

import { useState, useRef } from 'react'
import type { SimulationEvent, SimulationEventType, UserProfile } from '../engine/types'
import { newEventId, defaultEventLabel } from '../engine/events'
import { buildLifeEventRecommendations, generateAlgorithmicEvents, formatRecommendationsForLLM } from '../engine/lifeEventProbs'
import { chatComplete } from '../llm/client'
import { SYSTEM_PROMPT, buildContext } from '../llm/promptBuilder'
import { extractUpdate, applyUpdate } from '../llm/paramExtractor'
import type { SimulationParams } from '../engine/types'

interface Props {
  events: SimulationEvent[]
  onChange: (events: SimulationEvent[]) => void
  startYear: number
  profile: UserProfile
  params: SimulationParams
  onParamsChange: (updated: SimulationParams) => void
  onRunSimulation: () => void
  onScenarioChange?: (name: string | null) => void
}

const EVENT_CATALOG: Array<{
  type: SimulationEventType
  icon: string
  description: string
}> = [
  { type: 'promotion',        icon: '📈', description: 'Raise, promotion, new job' },
  { type: 'job-loss',         icon: '💼', description: 'Termination, layoff' },
  { type: 'sabbatical',       icon: '🏖️', description: 'Unpaid leave, career break' },
  { type: 'disability',       icon: '🩺', description: 'Partial income reduction' },
  { type: 'flat-bonus',       icon: '💵', description: 'Cash bonus, one-time or recurring' },
  { type: 'windfall',         icon: '🎁', description: 'Inheritance, gift, settlement' },
  { type: 'medical-emergency',icon: '🏥', description: 'Insured or uninsured emergency' },
  { type: 'major-expense',    icon: '💸', description: 'Car, renovation, travel, etc.' },
  { type: 'market-shock',     icon: '📉', description: 'Crash, portfolio loss' },
  { type: 'childcare',        icon: '👶', description: 'Daycare, after-school care' },
  { type: 'college-tuition',  icon: '🎓', description: "Child's college tuition" },
  { type: 'home-purchase',    icon: '🏠', description: 'Buy a home (adds mortgage)' },
  { type: 'long-term-care',   icon: '👴', description: 'Nursing, in-home care (late-stage)' },
  { type: 'divorce',          icon: '⚖️', description: 'Asset split + income change' },
]

type AddStep = 'pick-type' | 'configure'

function blankParams(type: SimulationEventType): SimulationEvent['params'] {
  switch (type) {
    case 'promotion':         return { newAnnualIncome: 0, raisePct: 0, recurEveryYears: 0 }
    case 'job-loss':          return { incomeFraction: 0, durationYears: 0 }
    case 'sabbatical':        return { incomeFraction: 0, durationYears: 1 }
    case 'disability':        return { incomeFraction: 0.4, durationYears: 2 }
    case 'flat-bonus':        return { amount: 10_000, recurEveryYears: 0 }
    case 'windfall':          return { amount: 50_000 }
    case 'medical-emergency': return { cost: 50_000, insured: true, deductible: 8_000, spreadYears: 1 }
    case 'major-expense':     return { amount: 30_000, spreadYears: 1 }
    case 'market-shock':      return { portfolioLossPct: 0.35, recoveryYears: 3, recoveryReturnPenalty: 0.04 }
    case 'childcare':         return { annualCost: 18_000, endYearOffset: 5 }
    case 'college-tuition':   return { annualCost: 35_000, endYearOffset: 4 }
    case 'home-purchase':     return { homePrice: 400_000, downPaymentPct: 0.20, mortgageRate: 0.065, mortgageTermYears: 30 }
    case 'long-term-care':    return { annualCost: 60_000, endYearOffset: 5 }
    case 'divorce':           return { assetRetentionFraction: 0.5 }
    default:                  return {}
  }
}

export function EventsPanel({ events, onChange, startYear, profile, params, onParamsChange, onRunSimulation, onScenarioChange }: Props) {
  const [addStep, setAddStep] = useState<AddStep | null>(null)
  const [draft, setDraft] = useState<Partial<SimulationEvent> | null>(null)
  const [simulating, setSimulating] = useState(false)
  const [simError, setSimError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const currentAge = profile.birthYear > 0 ? new Date().getFullYear() - profile.birthYear : 40
  const projectionYears = params.projectionYears

  async function handleSimulateRealistic() {
    setSimulating(true)
    setSimError(null)

    const abort = new AbortController()
    abortRef.current = abort

    const recs = buildLifeEventRecommendations(profile, projectionYears)
    const brief = formatRecommendationsForLLM(recs, currentAge, projectionYears, profile.childrenCount)

    const systemMsg = `${SYSTEM_PROMPT}\n\n${brief}`
    const userMsg = `Generate a realistic lifetime event plan for this person. Include likely events from the brief above. Use addEvents array. Clear any existing events first. Be realistic — this is a stress-test of an actual human life. Include parent care, inheritance, market downturns, job disruptions, medical events, and child-related costs based on their ${profile.childrenCount} children.`

    try {
      let fullResponse = ''
      await chatComplete(
        [
          { role: 'system', content: systemMsg },
          { role: 'user', content: `${buildContext(params)}\n\n${userMsg}` },
        ],
        delta => { fullResponse += delta },
        abort.signal,
      )

      const update = extractUpdate(fullResponse)
      const scenarioName = update?.scenario ?? 'Realistic US Lifetime'

      if (update?.update?.addEvents?.length) {
        const cleared = { ...params, events: [] }
        const updated = applyUpdate(cleared, update)
        onParamsChange(updated)
        onScenarioChange?.(scenarioName)
        setTimeout(onRunSimulation, 150)
      } else if (update?.update?.addEvent) {
        const updated = applyUpdate(params, update)
        onParamsChange(updated)
        onScenarioChange?.(scenarioName)
        setTimeout(onRunSimulation, 150)
      } else {
        // LLM returned no events — use seeded algorithmic fallback
        const generatedEvents = generateAlgorithmicEvents(recs, profile.birthYear)
        onParamsChange({ ...params, events: generatedEvents })
        onScenarioChange?.('Realistic US Lifetime (Statistical)')
        setTimeout(onRunSimulation, 150)
      }
    } catch (err: unknown) {
      if ((err as Error).name === 'AbortError') return
      // LLM unavailable — use seeded algorithmic fallback
      const generatedEvents = generateAlgorithmicEvents(recs, profile.birthYear)
      onParamsChange({ ...params, events: generatedEvents })
      onScenarioChange?.('Realistic US Lifetime (Statistical)')
      setSimError('LLM unavailable — used statistical model instead.')
      setTimeout(onRunSimulation, 150)
    } finally {
      setSimulating(false)
      abortRef.current = null
    }
  }

  function startAdd() {
    setAddStep('pick-type')
    setDraft(null)
  }

  function pickType(type: SimulationEventType) {
    setDraft({
      type,
      label: defaultEventLabel(type),
      startYearOffset: 0,
      params: blankParams(type),
    })
    setAddStep('configure')
  }

  function saveEvent() {
    if (!draft?.type) return
    const ev: SimulationEvent = {
      id: newEventId(),
      type: draft.type,
      label: draft.label || defaultEventLabel(draft.type),
      startYearOffset: draft.startYearOffset ?? 0,
      params: draft.params ?? {},
    }
    onChange([...events, ev])
    setAddStep(null)
    setDraft(null)
  }

  function removeEvent(id: string) {
    onChange(events.filter(e => e.id !== id))
  }

  function setDraftParam<K extends keyof SimulationEvent['params']>(key: K, value: SimulationEvent['params'][K]) {
    setDraft(d => d ? { ...d, params: { ...d.params, [key]: value } } : d)
  }

  return (
    <div className="events-panel">
      <div className="events-header">
        <span className="events-title">Life Events</span>
        <button
          className="events-simulate-btn"
          onClick={handleSimulateRealistic}
          disabled={simulating}
          title="AI generates a statistically realistic lifetime event plan based on your age and US demographics"
        >
          {simulating ? '⏳ Simulating…' : '🎲 Simulate US Avg Lifetime'}
        </button>
        <button className="events-add-btn" onClick={startAdd} disabled={simulating}>+ Add</button>
      </div>
      {simError && <p className="events-sim-error">{simError}</p>}

      {/* Event list */}
      <div className="events-list">
        {events.length === 0 && addStep === null && (
          <div className="events-empty">
            <p>No events yet.</p>
            <p className="events-empty-hint">Add career changes, market shocks, medical events, and more to stress-test your plan.</p>
          </div>
        )}
        {events.map(ev => {
          const meta = EVENT_CATALOG.find(c => c.type === ev.type)
          return (
            <div key={ev.id} className="event-row">
              <span className="event-icon">{meta?.icon ?? '📌'}</span>
              <div className="event-row-info">
                <span className="event-row-label">{ev.label}</span>
                <span className="event-row-year">Year {ev.startYearOffset + 1} · {startYear + ev.startYearOffset}</span>
              </div>
              <button className="event-remove-btn" onClick={() => removeEvent(ev.id)} title="Remove">✕</button>
            </div>
          )
        })}
      </div>

      {/* Add wizard — pick type */}
      {addStep === 'pick-type' && (
        <div className="events-wizard">
          <div className="wizard-header">
            <span>Select event type</span>
            <button className="wizard-cancel" onClick={() => setAddStep(null)}>✕</button>
          </div>
          <div className="wizard-type-grid">
            {EVENT_CATALOG.map(c => (
              <button
                key={c.type}
                className="wizard-type-btn"
                onClick={() => pickType(c.type)}
              >
                <span className="wizard-type-icon">{c.icon}</span>
                <span className="wizard-type-name">{defaultEventLabel(c.type)}</span>
                <span className="wizard-type-desc">{c.description}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add wizard — configure */}
      {addStep === 'configure' && draft && (
        <div className="events-wizard">
          <div className="wizard-header">
            <span>{EVENT_CATALOG.find(c => c.type === draft.type)?.icon} {defaultEventLabel(draft.type!)}</span>
            <button className="wizard-cancel" onClick={() => setAddStep('pick-type')}>← Back</button>
          </div>
          <div className="wizard-form">
            <WizardField label="Label">
              <input
                type="text"
                value={draft.label ?? ''}
                onChange={e => setDraft(d => d ? { ...d, label: e.target.value } : d)}
              />
            </WizardField>
            <WizardField label={`Start year (offset, 0=${startYear})`}>
              <input
                type="number" min={0} max={29}
                value={draft.startYearOffset ?? 0}
                onChange={e => setDraft(d => d ? { ...d, startYearOffset: Number(e.target.value) } : d)}
              />
            </WizardField>
            <EventParamForm type={draft.type!} params={draft.params ?? {}} setParam={setDraftParam} />
          </div>
          <button className="wizard-save-btn" onClick={saveEvent}>Add Event</button>
        </div>
      )}
    </div>
  )
}

// ─── Per-type parameter form ──────────────────────────────────────────────────

function EventParamForm({
  type, params, setParam,
}: {
  type: SimulationEventType
  params: SimulationEvent['params']
  setParam: <K extends keyof SimulationEvent['params']>(k: K, v: SimulationEvent['params'][K]) => void
}) {
  switch (type) {
    case 'promotion':
      return <>
        <WizardField label="New annual W2 income ($, 0=use raise %)">
          <input type="number" step={5000} min={0} value={params.newAnnualIncome ?? 0}
            onChange={e => setParam('newAnnualIncome', Number(e.target.value))} />
        </WizardField>
        <WizardField label="— OR — % raise (0.10 = 10%)">
          <input type="number" step={0.01} min={0} max={1} value={params.raisePct ?? 0}
            onChange={e => setParam('raisePct', Number(e.target.value))} />
        </WizardField>
        <WizardField label="New equity comp ($, 0=unchanged)">
          <input type="number" step={5000} min={0} value={params.newEquityComp ?? 0}
            onChange={e => setParam('newEquityComp', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Recur every N years (0=one-time)">
          <input type="number" min={0} max={10} value={params.recurEveryYears ?? 0}
            onChange={e => setParam('recurEveryYears', Number(e.target.value))} />
        </WizardField>
      </>

    case 'job-loss':
    case 'sabbatical':
      return <>
        <WizardField label="Income fraction retained (0=zero, 0.4=40%)">
          <input type="number" step={0.1} min={0} max={1} value={params.incomeFraction ?? 0}
            onChange={e => setParam('incomeFraction', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Duration (years, 0=permanent)">
          <input type="number" min={0} max={20} value={params.durationYears ?? 0}
            onChange={e => setParam('durationYears', Number(e.target.value))} />
        </WizardField>
      </>

    case 'disability':
      return <>
        <WizardField label="Income fraction retained (0.4 = keep 40%)">
          <input type="number" step={0.1} min={0} max={1} value={params.incomeFraction ?? 0.4}
            onChange={e => setParam('incomeFraction', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Duration (years, 0=permanent)">
          <input type="number" min={0} max={20} value={params.durationYears ?? 2}
            onChange={e => setParam('durationYears', Number(e.target.value))} />
        </WizardField>
      </>

    case 'flat-bonus':
      return <>
        <WizardField label="Bonus amount ($)">
          <input type="number" step={5000} min={0} value={params.amount ?? 10_000}
            onChange={e => setParam('amount', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Recur every N years (0=one-time)">
          <input type="number" min={0} max={10} value={params.recurEveryYears ?? 0}
            onChange={e => setParam('recurEveryYears', Number(e.target.value))} />
        </WizardField>
      </>

    case 'windfall':
      return <>
        <WizardField label="Amount ($)">
          <input type="number" step={10000} min={0} value={params.amount ?? 50_000}
            onChange={e => setParam('amount', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Spread over N years (1=lump sum)">
          <input type="number" min={1} max={10} value={params.spreadYears ?? 1}
            onChange={e => setParam('spreadYears', Number(e.target.value))} />
        </WizardField>
      </>

    case 'medical-emergency':
      return <>
        <WizardField label="Total cost ($)">
          <input type="number" step={5000} min={0} value={params.cost ?? 50_000}
            onChange={e => setParam('cost', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Insured?">
          <select value={params.insured ? 'yes' : 'no'}
            onChange={e => setParam('insured', e.target.value === 'yes')}>
            <option value="yes">Yes (capped at deductible)</option>
            <option value="no">No (full cost)</option>
          </select>
        </WizardField>
        {params.insured && (
          <WizardField label="Annual deductible / max OOP ($)">
            <input type="number" step={1000} min={0} value={params.deductible ?? 8_000}
              onChange={e => setParam('deductible', Number(e.target.value))} />
          </WizardField>
        )}
        <WizardField label="Spread over N years (1=immediate)">
          <input type="number" min={1} max={5} value={params.spreadYears ?? 1}
            onChange={e => setParam('spreadYears', Number(e.target.value))} />
        </WizardField>
      </>

    case 'major-expense':
      return <>
        <WizardField label="Total amount ($)">
          <input type="number" step={5000} min={0} value={params.amount ?? 30_000}
            onChange={e => setParam('amount', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Spread over N years (1=one-time)">
          <input type="number" min={1} max={10} value={params.spreadYears ?? 1}
            onChange={e => setParam('spreadYears', Number(e.target.value))} />
        </WizardField>
      </>

    case 'market-shock':
      return <>
        <WizardField label="Portfolio loss (0.35 = 35% drop)">
          <input type="number" step={0.05} min={0} max={0.9} value={params.portfolioLossPct ?? 0.35}
            onChange={e => setParam('portfolioLossPct', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Recovery years">
          <input type="number" min={1} max={10} value={params.recoveryYears ?? 3}
            onChange={e => setParam('recoveryYears', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Return penalty during recovery">
          <input type="number" step={0.01} min={0} max={0.1} value={params.recoveryReturnPenalty ?? 0.04}
            onChange={e => setParam('recoveryReturnPenalty', Number(e.target.value))} />
        </WizardField>
      </>

    case 'childcare':
    case 'college-tuition':
    case 'long-term-care':
      return <>
        <WizardField label="Annual cost ($)">
          <input type="number" step={1000} min={0} value={params.annualCost ?? 20_000}
            onChange={e => setParam('annualCost', Number(e.target.value))} />
        </WizardField>
        <WizardField label="End year offset from start">
          <input type="number" min={1} max={20} value={params.endYearOffset ?? 4}
            onChange={e => setParam('endYearOffset', Number(e.target.value))} />
        </WizardField>
      </>

    case 'home-purchase':
      return <>
        <WizardField label="Home price ($)">
          <input type="number" step={10000} min={0} value={params.homePrice ?? 400_000}
            onChange={e => setParam('homePrice', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Down payment (0.20 = 20%)">
          <input type="number" step={0.05} min={0.03} max={1} value={params.downPaymentPct ?? 0.20}
            onChange={e => setParam('downPaymentPct', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Mortgage rate (0.065 = 6.5%)">
          <input type="number" step={0.0025} min={0.02} max={0.15} value={params.mortgageRate ?? 0.065}
            onChange={e => setParam('mortgageRate', Number(e.target.value))} />
        </WizardField>
        <WizardField label="Term (years)">
          <input type="number" min={10} max={30} step={5} value={params.mortgageTermYears ?? 30}
            onChange={e => setParam('mortgageTermYears', Number(e.target.value))} />
        </WizardField>
      </>

    case 'divorce':
      return <>
        <WizardField label="Asset fraction you retain (0.5 = 50%)">
          <input type="number" step={0.05} min={0} max={1} value={params.assetRetentionFraction ?? 0.5}
            onChange={e => setParam('assetRetentionFraction', Number(e.target.value))} />
        </WizardField>
      </>

    default:
      return null
  }
}

function WizardField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="wizard-field">
      <span className="wizard-field-label">{label}</span>
      {children}
    </label>
  )
}
